/* WARNING !!!
 * DO NOT REORGANIZE THE ORDER OF IMPORTS
 * -> IT THROWS "cannot access upAxios before init" WITHOUT AN OBVIOUS REASON
 */

import { LogoutRequest } from '@odiupsk/up-api-client'
import { AccessTokenStateEnum } from '../../../../types/enums'
import { authApi } from '../../api'
import { clientSecret, CLIENT_ID } from '../../../config'
import { ApiAuthRepository } from '../../api/ApiAuthRepository'
import { formatDateTime } from '../../utils/format'
import { PersistentLogger } from '../logger/PersistentLogger'

const EXPIRES_IN_BUFFER_SECONDS = 5 * 60 // 5 min

export class Auth {
  static async login(username: string, password: string) {
    const response = await ApiAuthRepository.login(username, password)

    const { access_token } = response
    const { tokenState, debugData, userId } = this.decodeDataFromAccessToken(access_token)

    // Handle BAD new token
    if (this.isBadTokenState(tokenState)) {
      PersistentLogger.log({
        message: `ERROR FETCHING TOKEN ON LOGIN (${tokenState})`,
        origin: 'login',
        debugData,
      })
      throw new Error(tokenState)
    }

    this.saveTokens(access_token as string, userId)

    return {
      userId,
    }
  }

  static async handleBadNewToken(props: {
    redirect?: boolean
    origin?: string
    messageError: string
    tokenState?: AccessTokenStateEnum
    debugData?: any
  }) {
    const {
      redirect,
      origin,
      messageError,
      tokenState,
      debugData = { 'no-debug-data': true },
    } = props

    PersistentLogger.log({
      message: `${messageError}${tokenState ? ` (${tokenState})` : ''}`,
      origin,
      debugData,
    })

    this.removeAccessToken()
    location.href = redirect ? `/auth?redirect=${encodeURIComponent(location.pathname)}` : `/auth`
    throw new Error(tokenState)
  }

  static isBadTokenState(tokenState: AccessTokenStateEnum) {
    return [
      AccessTokenStateEnum.missingToken,
      AccessTokenStateEnum.missingExpiresIn,
      AccessTokenStateEnum.expiredToken,
    ].includes(tokenState)
  }

  static async fetchAndProcessNewAccessToken(props: {
    logger: {
      messageStart: string
      messageEnd: string
      messageError: string
    }
    origin?: string
    redirect?: boolean
  }) {
    const { logger, origin, redirect } = props
    const { debugData: debugDataCurrentToken } = this.getAccessTokenData()

    PersistentLogger.log({
      message: `${logger.messageStart}`,
      origin,
      debugDataCurrentToken,
    })

    // TODO - INTERCEPTOR Z src/config/index TOHLE PRERUSI PRI A DALE SE NEPOKRACUJE! -> NUTNO FCE "PROCES TOKEN A HODIT TO I DO INTERCEPTORU AT VYMAZE STORAGE!"
    const response = await authApi.postToken({
      client_id: CLIENT_ID,
      client_secret: clientSecret,
      grant_type: 'refresh_token',
    })

    const { access_token } = response.data
    const { tokenState, debugData: debugDataNewToken } = this.decodeDataFromAccessToken(
      access_token
    )

    // Handle BAD new token
    if (this.isBadTokenState(tokenState)) {
      return this.handleBadNewToken({
        redirect,
        origin,
        messageError: logger.messageError,
        tokenState,
        debugData: debugDataNewToken,
      })
    }

    // Handle VALID new token
    this.saveTokens(access_token)

    PersistentLogger.log({
      message: `${logger.messageEnd}`,
      origin,
      debugDataNewToken,
    })
  }

  static async fetchNewAccessToken(props: { origin: string }) {
    const { origin } = props

    return this.fetchAndProcessNewAccessToken({
      origin,
      logger: {
        messageStart: `FETCH NEW ACCESS_TOKEN (fetchNewAccessToken)`,
        messageEnd: `NEW ACCESS_TOKEN FETCHED (fetchNewAccessToken)`,
        messageError: `ERROR FETCHING NEW ACCESS TOKEN (fetchNewAccessToken)`,
      },
      redirect: true,
    })
  }

  static async fetchNewAccessTokenWithoutRedirect(props: { origin: string }) {
    const { origin } = props

    return this.fetchAndProcessNewAccessToken({
      origin,
      logger: {
        messageStart: `FETCH NEW ACCESS_TOKEN (fetchNewAccessTokenWithoutRedirect)`,
        messageEnd: `NEW ACCESS_TOKEN FETCHED (fetchNewAccessTokenWithoutRedirect)`,
        messageError: `ERROR FETCHING NEW ACCESS TOKEN (fetchNewAccessTokenWithoutRedirect)`,
      },
      redirect: false,
    })
  }

  static saveTokens(accessToken: string, userId?: string) {
    if (userId) this.setUserId(userId)
    this.setAccessToken(accessToken)
  }

  static decodeToken(
    token: string
  ): {
    userId: string
    expiresIn: number
    resetPassword: boolean
    changeDummyEmail: boolean
  } {
    const {
      sub: userId,
      exp: expiresIn,
      reset_password: resetPassword,
      change_dummy_email: changeDummyEmail,
    } = JSON.parse(atob(this.base64UrlToBase64(token.split('.')[1])))
    return { userId, expiresIn, resetPassword, changeDummyEmail }
  }

  static base64UrlToBase64 = function(input: string) {
    // Replace non-url compatible chars with base64 standard chars
    let token = input.replace(/-/g, '+').replace(/_/g, '/')

    // Pad out with standard base64 required padding characters
    const pad = token.length % 4

    if (pad) {
      if (pad === 1) {
        throw new Error(
          'InvalidLengthError: Input base64url string is the wrong length to determine padding'
        )
      }
      token += new Array(5 - pad).join('=')
    }

    return token
  }

  static forcePasswordReset(token: string) {
    const { resetPassword } = this.decodeToken(token)
    return resetPassword
  }

  static forceChangeEmail(token: string) {
    const { changeDummyEmail } = this.decodeToken(token)
    return changeDummyEmail
  }

  static getAccessToken() {
    return sessionStorage.getItem('auth.accessToken')
  }

  static setAccessToken(token: string) {
    return sessionStorage.setItem('auth.accessToken', token)
  }

  static removeAccessToken() {
    this.setAccessToken('')
    this.setUserId('')
  }

  static getAccessTokenData() {
    const token = this.getAccessToken()
    return this.decodeDataFromAccessToken(token)
  }

  static getUserId() {
    return localStorage.getItem('auth.userId')
  }

  static setUserId(id: string) {
    return localStorage.setItem('auth.userId', id)
  }

  static async logout() {
    const body: LogoutRequest = {
      client_id: CLIENT_ID,
      client_secret: clientSecret,
    }
    await authApi.api.post(`/auth/logout`, body)
    this.removeAccessToken()
    location.href = '/auth'
  }

  static decodeDataFromAccessToken(token: string | null | undefined) {
    let debugData = {}
    let tokenState: AccessTokenStateEnum
    let userId

    const nowSeconds = Math.floor(Date.now() / 1000)

    if (!token) {
      // Missing token
      tokenState = AccessTokenStateEnum.missingToken
      debugData = {
        message: 'NO ACCESS TOKEN',
        checkedAt: formatDateTime(new Date(nowSeconds * 1000)),
      }
    } else {
      const decodedToken = this.decodeToken(token)

      userId = decodedToken?.userId

      if (!decodedToken?.expiresIn) {
        // Missing expire date
        tokenState = AccessTokenStateEnum.missingExpiresIn
        debugData = {
          message: 'NO EXPIRE DATE',
          checkedAt: formatDateTime(new Date(nowSeconds * 1000)),
        }
      } else {
        // Token exists and has an expire date - check if it expired
        const expiresInTimestampSeconds = decodedToken.expiresIn
        const expiresInTimestampSecondsWithBuffer =
          expiresInTimestampSeconds - EXPIRES_IN_BUFFER_SECONDS

        const isExpired = nowSeconds >= expiresInTimestampSecondsWithBuffer

        tokenState = isExpired ? AccessTokenStateEnum.expiredToken : AccessTokenStateEnum.validToken

        debugData = {
          expireBuffer: `${EXPIRES_IN_BUFFER_SECONDS / 60} min`,
          '1_checkedAt': formatDateTime(new Date(nowSeconds * 1000)),
          '2_refreshAt': formatDateTime(new Date(expiresInTimestampSecondsWithBuffer * 1000)),
          '3_expiresAt': formatDateTime(new Date(expiresInTimestampSeconds * 1000)),
        }
      }
    }

    return {
      token,
      tokenState,
      userId,
      debugData,
    }
  }
}
